import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import * as React from "react";
import { useEffect, useState } from "react";
import { X } from "react-bootstrap-icons";
import { useDispatch, useSelector } from "react-redux";
import { EBracketTypes } from "../../../../shared/enums/utility.enums";
import { IAppConfig } from "../../../../shared/types/config.types";
import { RootState } from "../../../../state-management/root-reducer";
import {
  IPinModeState,
  IRoutingState,
  updateDrawerState,
} from "../../../../state-management/slices/global/global.slice";
import { AppDispatch } from "../../../../state-management/store";
import { getBracketTypeDisplayLabel } from "../../../../state-management/utils/state/outages.utils";
import Chevron from "../../../../theme/assets/chevron-right.svg";
import ColorsPalette from "../../../../theme/colors/Colors.palette";

import ButtonStyles from "../../../button/Button.styles";
import STREETLIGHT_ICON from "../../../icon/streetlight-icon/StreetlightIcon.component";
import { EMarkerStatuses } from "../../Map.constants";
import { ISelectedMarker } from "../../Map.types";
import PopupStyles from "./Popup.styles";
import { IMarkerAttributes, IPopupProps } from "./Popup.types";

const getStreetlightLabel = (
  bracketTypeData: string,
  pinMode: IPinModeState,
  routing: IRoutingState
): string => {
  if (pinMode.enabled) {
    return "New Streetlight";
  }

  if (routing.selectedOutage) {
    const { bracketType } = routing.selectedOutage.utility as any;
    return `${bracketType} Streetlight`;
  }

  if (
    bracketTypeData === EBracketTypes.OTHER ||
    bracketTypeData === EBracketTypes.UNKNOWN
  ) {
    return "Streetlight";
  }

  return `${getBracketTypeDisplayLabel(bracketTypeData)} Streetlight`;
};

const renderHeader = (
  routing: IRoutingState,
  pinMode: IPinModeState,
  markerAttributes: IMarkerAttributes,
  address: string
):
  | React.ReactElement<any, string | React.JSXElementConstructor<any>>
  | React.ReactNodeArray => {
  const { markerStatus, bracketType, GISID } = markerAttributes;

  return (
    <>
      <div className="title-container">
        <div>{getStreetlightLabel(bracketType, pinMode, routing)}</div>
        {markerStatus === EMarkerStatuses.COMPLETED && !pinMode.enabled && (
          <div className="sub-text">Streetlight ID: {GISID || "Pending"}</div>
        )}
      </div>
      <div className="sub-text">{address}</div>
    </>
  );
};

const renderContent = (
  markerStatus: EMarkerStatuses,
  reportedDate: string | null,
  sapNotificationId: string | null,
  GISID: string,
  dispatch: AppDispatch,
  sendDataToGTM: any,
  layerId?: string,
  appConfig?: IAppConfig | null
): React.ReactElement => {
  if (
    markerStatus === EMarkerStatuses.REPORTED ||
    markerStatus === EMarkerStatuses.IN_PROGRESS
  ) {
    return (
      <PopupStyles.ReportedContainer className="reported-container">
        <ul>
          <li>
            <span className="label">Status:</span>
            <span className="value">Reported/In Progress</span>
          </li>
          <li>
            <span className="label">Reported:</span>
            <span>{reportedDate || "Not Available"}</span>
          </li>
          <li>
            <span className="label">Notification #:</span>
            <span>{sapNotificationId || "Pending"}</span>
          </li>
          <li>
            <span className="label">Streetlight ID:</span>
            <span>{GISID || "Pending"}</span>
          </li>
        </ul>
      </PopupStyles.ReportedContainer>
    );
  } else if (markerStatus === EMarkerStatuses.EXTENDED_REPAIR) {
    return (
      <PopupStyles.ReportedContainer className="extended-repair-container">
        <ul>
          <li>
            <span className="label">Status:</span>
            <span className="value">Extended Repair</span>
          </li>
          <li>
            <span className="label">Reported:</span>
            <span>{reportedDate || "Not Available"}</span>
          </li>
          <li>
            <span className="label">Streetlight ID:</span>
            <span>{GISID || "Pending"}</span>
          </li>
        </ul>
        <p>
          Our crews have determined that this streetlight will need an extended
          amount of time to repair
        </p>
      </PopupStyles.ReportedContainer>
    );
  } else if (markerStatus === EMarkerStatuses.NON_CE) {
    return layerId === appConfig?.esriGrandRapidsLayerId ? (
      <PopupStyles.ReportedContainer className="non-ce-container">
        <p>
          This light is maintained by the city of Grand Rapids, please call{" "}
          <a href="tel:616-456-3000">616-456-3000</a>, to report an issue.
        </p>
      </PopupStyles.ReportedContainer>
    ) : (
      <PopupStyles.ReportedContainer className="non-ce-container">
        <p>
          This light is not maintained by Consumers Energy. It may be privately
          owned or maintained by your local municipality.
        </p>
      </PopupStyles.ReportedContainer>
    );
  } else {
    return (
      <ButtonStyles.ButtonContainer className="button-container">
        <ButtonStyles.LinkButton
          className="link-button"
          onClick={(event) => {
            event.preventDefault();
            dispatch(updateDrawerState(true));
            sendDataToGTM({
              event: "eventTracking",
              category: "Outage",
              action: "Street Light",
              label: "Report Outage Clicked",
            });
          }}
          title="Report Outage Button"
        >
          <span>Report Outage</span>
          <img src={Chevron} alt="Report outage icon" />
        </ButtonStyles.LinkButton>
      </ButtonStyles.ButtonContainer>
    );
  }
};

const Popup: React.FC<IPopupProps> = ({
  address,
  markerAttributes,
  reportedDate,
  sapNotificationId,
  layerId,
  onClose,
}: IPopupProps) => {
  const dispatch = useDispatch();
  const sendDataToGTM = useGTMDispatch();
  const { markerStatus, bracketType, GISID } = markerAttributes;

  // App State
  const { appConfig } = useSelector((state: RootState) => state.globalState);
  const { pinMode, routing } = useSelector(
    (state: RootState) => state.globalState.ui
  );

  // Local State
  const [hasOutage, setHasOutage] = useState(0);
  const [isExtendedRepair, setIsExtendedRepair] = useState(false);
  const [isNonCE, setIsNonCE] = useState(false);

  useEffect(() => {
    if (
      markerStatus === EMarkerStatuses.REPORTED ||
      markerStatus === EMarkerStatuses.IN_PROGRESS
    ) {
      setHasOutage(1);
    }

    if (markerStatus === EMarkerStatuses.EXTENDED_REPAIR) {
      setIsExtendedRepair(true);
    }

    if (markerStatus === EMarkerStatuses.NON_CE) {
      setIsNonCE(true);
    }
  }, [markerStatus]);

  return (
    <PopupStyles.MainContainer className="popup-container">
      <PopupStyles.IconContainer>
        {!isExtendedRepair && !isNonCE && (
          <STREETLIGHT_ICON
            className="corner-icon"
            type={bracketType}
            reported={hasOutage}
          />
        )}
        {isExtendedRepair && (
          <STREETLIGHT_ICON
            className="corner-icon"
            type={bracketType}
            reported={0}
            bgFill={ColorsPalette.MapMarkers.extendedRepairStroke}
          />
        )}
        {isNonCE && (
          <STREETLIGHT_ICON
            className="corner-icon"
            type={bracketType}
            reported={0}
            bgFill={ColorsPalette.MapMarkers.nonCEStroke}
          />
        )}
      </PopupStyles.IconContainer>

      <PopupStyles.ContentContainer>
        {renderHeader(routing, pinMode, markerAttributes, address)}

        {renderContent(
          markerStatus,
          reportedDate,
          sapNotificationId,
          GISID,
          dispatch,
          sendDataToGTM,
          layerId,
          appConfig
        )}
      </PopupStyles.ContentContainer>

      <PopupStyles.IconContainer
        className="close-button-container"
        justify="flex-end"
      >
        <PopupStyles.CloseButton onClick={onClose} title="Close Button">
          <X height="24" width="24" />
        </PopupStyles.CloseButton>
      </PopupStyles.IconContainer>
    </PopupStyles.MainContainer>
  );
};

export default Popup;
